const { useCallback, useEffect, useRef } = require('react');
const SsoUtils = require('utils/sso');

const getInAppBrowser = () => window.cordova?.InAppBrowser;

const normalizeState = (state) => (state || '').replace(/#$/, '');

const internals = {};
exports.internals = internals;

exports.useCordovaOidcBrowser = function useCordovaOidcBrowser(props) {

    const {
        redirectUri,
        onCallback,
        onCancel,
        onError
    } = props || {};

    const browserWindowRef = useRef(null);
    const handledRef = useRef(false);

    const cleanupBrowser = useCallback(() => {

        const browser = browserWindowRef.current;

        if (!browser) {
            return;
        }

        try {
            browser.removeEventListener('loadstart', browser.__nearpeerLoadStart);
        }
        catch (err) {}

        try {
            browser.removeEventListener('exit', browser.__nearpeerExit);
        }
        catch (err) {}

        try {
            browser.close();
        }
        catch (err) {}

        browserWindowRef.current = null;
    }, []);

    useEffect(() => {

        return () => {

            cleanupBrowser();
        };
    }, [cleanupBrowser]);

    const openUrl = useCallback((url) => {

        if (!window.cordova) {
            SsoUtils.recordSsoRuntime('cordova_sso_fallback_to_window_location', {
                reason: 'window.cordova_missing',
                redirectUri,
                urlHost: internals.safeUrlPart(url, 'host')
            });
            window.location = url;
            return;
        }

        const InAppBrowser = getInAppBrowser();

        if (!InAppBrowser) {
            SsoUtils.recordSsoRuntime('cordova_sso_fallback_to_window_location', {
                reason: 'in_app_browser_missing',
                redirectUri,
                urlHost: internals.safeUrlPart(url, 'host')
            });
            window.location = url;
            return;
        }

        handledRef.current = false;
        cleanupBrowser();

        SsoUtils.recordSsoRuntime('cordova_sso_open_browser', {
            redirectUri,
            loginUrlHost: internals.safeUrlPart(url, 'host'),
            loginUrlPath: internals.safeUrlPart(url, 'pathname'),
            expectedRedirect: internals.describeRedirect(redirectUri)
        });

        const browser = InAppBrowser.open(
            url,
            '_blank',
            'location=no,clearsessioncache=no,clearcache=no'
        );

        browser.__nearpeerLoadStart = (evt) => {

            if (handledRef.current) {
                return;
            }

            const redirectMatch = internals.getRedirectMatch(evt?.url, redirectUri);

            SsoUtils.recordSsoRuntime('cordova_sso_loadstart', {
                urlHost: internals.safeUrlPart(evt?.url, 'host'),
                urlPath: internals.safeUrlPart(evt?.url, 'pathname'),
                redirectMatch
            });

            if (!redirectMatch.matches) {
                return;
            }

            handledRef.current = true;

            const result = internals.parseCallbackUrl(evt?.url);

            cleanupBrowser();

            if (result.redirectToLogin) {
                onCancel?.();
                return;
            }

            if (result.error) {
                SsoUtils.recordSsoRuntime('cordova_sso_callback_error', result);
                onError?.(result);
                return;
            }

            if (!result.code || !result.state) {
                SsoUtils.recordSsoRuntime('cordova_sso_invalid_callback_params', {
                    hasCode: !!result.code,
                    hasState: !!result.state,
                    callbackHost: internals.safeUrlPart(evt?.url, 'host'),
                    callbackPath: internals.safeUrlPart(evt?.url, 'pathname')
                });

                onError?.({
                    error: 'invalid_callback_params',
                    errorDescription: 'Missing code or state in callback URL'
                });
                return;
            }

            SsoUtils.recordSsoRuntime('cordova_sso_callback_received', {
                hasCode: !!result.code,
                hasState: !!result.state,
                callbackHost: internals.safeUrlPart(evt?.url, 'host'),
                callbackPath: internals.safeUrlPart(evt?.url, 'pathname')
            });

            onCallback?.({
                code: result.code,
                state: result.state,
                callbackUrl: evt?.url
            });
        };

        browser.__nearpeerExit = () => {
            if (handledRef.current) {
                return;
            }

            handledRef.current = true;
            cleanupBrowser();

            SsoUtils.recordSsoRuntime('cordova_sso_browser_exit', {
                redirectUri
            });

            onCancel?.();
        };

        browser.addEventListener('loadstart', browser.__nearpeerLoadStart);
        browser.addEventListener('exit', browser.__nearpeerExit);

        browserWindowRef.current = browser;
    }, [cleanupBrowser, onCallback, onCancel, onError, redirectUri]);

    return {
        openUrl,
        close: cleanupBrowser
    };
};

internals.parseCallbackUrl = (url) => {

    try {
        const parsed = new URL(url);
        return {
            code: parsed.searchParams.get('code'),
            state: normalizeState(parsed.searchParams.get('state')),
            error: parsed.searchParams.get('error'),
            errorDescription: parsed.searchParams.get('error_description'),
            redirectToLogin: parsed.searchParams.get('redirect_to_login') === 'true'
                || parsed.searchParams.get('redirect_to_login') === '1'
        };
    }
    catch (err) {
        return {
            code: null,
            state: '',
            error: 'invalid_callback_url',
            errorDescription: err?.message
        };
    }
};

internals.normalizeCallbackHost = (host) => (host || '').replace(/^www\./, '');
internals.normalizeCallbackPath = (path) => {

    if (!path || path === '/') {
        return path || '/';
    }

    return path.replace(/\/+$/, '');
};

internals.normalizeCallbackPort = (url) => {

    if (url.port) {
        return url.port;
    }

    if (url.protocol === 'https:') {
        return '443';
    }

    if (url.protocol === 'http:') {
        return '80';
    }

    return '';
};

internals.isExpectedRedirect = (url, redirectUri) => internals.getRedirectMatch(url, redirectUri).matches;

internals.getRedirectMatch = (url, redirectUri) => {

    if (!url || !redirectUri) {
        return {
            matches: false,
            reason: 'missing_url_or_redirect_uri',
            current: internals.describeRedirect(url),
            expected: internals.describeRedirect(redirectUri)
        };
    }

    try {
        const current = new URL(url);
        const expected = new URL(redirectUri);
        const details = {
            current: internals.describeRedirect(url),
            expected: internals.describeRedirect(redirectUri),
            protocolMatches: current.protocol === expected.protocol,
            hostMatches: internals.normalizeCallbackHost(current.hostname) === internals.normalizeCallbackHost(expected.hostname),
            portMatches: internals.normalizeCallbackPort(current) === internals.normalizeCallbackPort(expected),
            pathMatches: internals.normalizeCallbackPath(current.pathname) === internals.normalizeCallbackPath(expected.pathname)
        };

        return {
            ...details,
            matches: details.protocolMatches && details.hostMatches && details.portMatches && details.pathMatches
        };
    }
    catch (err) {
        return {
            matches: false,
            reason: err?.message || 'invalid_url',
            current: internals.describeRedirect(url),
            expected: internals.describeRedirect(redirectUri)
        };
    }
};

internals.describeRedirect = (url) => {

    try {
        const parsed = new URL(url);

        return {
            protocol: parsed.protocol,
            host: parsed.host,
            hostname: parsed.hostname,
            port: internals.normalizeCallbackPort(parsed),
            pathname: internals.normalizeCallbackPath(parsed.pathname)
        };
    }
    catch (err) {
        return {
            raw: url || null,
            error: err?.message || null
        };
    }
};

internals.safeUrlPart = (url, part) => {

    try {
        return new URL(url)[part] || null;
    }
    catch (err) {
        return null;
    }
};
